import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { InquiryDialog } from "@/components/InquiryDialog";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ZoomIn, X, Move } from "lucide-react";
import { mediaUrl } from "@/lib/api";

export default function AdDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, formatPrice } = useApp();
  const [ad, setAd] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [hoverZoom, setHoverZoom] = useState(false);
  const [zooming, setZooming] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [lbHoverEnabled, setLbHoverEnabled] = useState(false);
  const [lbHovering, setLbHovering] = useState(false);
  const [lbPos, setLbPos] = useState({ x: 50, y: 50 });
  const imgWrap = useRef(null);

  const lbMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setLbPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  const onMove = (e) => {
    if (!hoverZoom || !imgWrap.current) return;
    const r = imgWrap.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setPos({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/ads/${id}`).then((r) => setAd(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (notFound) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-[1400px] mx-auto px-8 py-32 text-center">
          <p className="font-serif text-4xl">404</p>
          <button data-testid="back-home" onClick={() => navigate("/")} className="mt-6 underline text-[color:var(--c-accent)]">{t("back")}</button>
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="App">
        <Header />
        <div className="max-w-[1400px] mx-auto px-8 py-20 grid lg:grid-cols-2 gap-12">
          <div className="aspect-[4/5] bg-[color:var(--c-surface)] animate-pulse" />
          <div className="space-y-4"><div className="h-10 w-2/3 bg-[color:var(--c-surface)] animate-pulse" /><div className="h-40 bg-[color:var(--c-surface)] animate-pulse" /></div>
        </div>
      </div>
    );
  }

  const facts = [
    [t("brand_label"), ad.brand],
    [t("topic"), ad.category],
    [t("era"), ad.era],
    [t("ad_language"), ad.ad_language],
    [t("condition"), ad.condition],
    [t("dimensions"), ad.dimensions],
  ].filter(([, v]) => v);

  return (
    <div className="App">
      <Header />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <button
          data-testid="back-to-collection"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-[color:var(--c-ink2)] hover:text-[color:var(--c-accent)] transition-colors mb-8"
        >
          <ArrowLeft size={16} /> {t("back")}
        </button>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Image with hover magnifier */}
          <div className="pv-in">
            <div
              ref={imgWrap}
              data-testid="detail-image"
              onClick={() => { if (!hoverZoom) { setLbHoverEnabled(false); setLbHovering(false); setLightbox(true); } }}
              onMouseEnter={() => hoverZoom && setZooming(true)}
              onMouseLeave={() => setZooming(false)}
              onMouseMove={onMove}
              className="relative block w-full overflow-hidden border border-[color:var(--c-ink)] bg-[color:var(--c-surface)] cursor-zoom-in aspect-[4/5]"
            >
              <img src={mediaUrl(ad.image)} alt={ad.title} className={`w-full h-full object-cover transition-opacity duration-200 ${hoverZoom && zooming ? "opacity-0" : "opacity-100"}`} />
              {hoverZoom && zooming && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ backgroundImage: `url(${mediaUrl(ad.image)})`, backgroundRepeat: "no-repeat", backgroundSize: "220%", backgroundPosition: `${pos.x}% ${pos.y}%` }}
                />
              )}
              <span className="absolute bottom-3 left-3 flex items-center gap-1 bg-[color:var(--c-glass)] text-[color:var(--c-ink)] text-[0.6rem] tracking-widest uppercase px-2 py-1 font-semibold pointer-events-none">
                <ZoomIn size={11} /> {hoverZoom ? t("hover_zoom") : t("click_enlarge")}
              </span>
              <button
                type="button"
                data-testid="hover-zoom-toggle"
                onClick={(e) => { e.stopPropagation(); setHoverZoom((v) => !v); setZooming(false); }}
                title={t("hover_zoom")}
                className={`absolute bottom-3 right-3 z-10 flex items-center gap-1 px-2 py-1 text-[0.6rem] tracking-widest uppercase font-semibold border transition-colors ${hoverZoom ? "bg-[color:var(--c-accent)] text-white border-[color:var(--c-accent)]" : "bg-[color:var(--c-glass)] text-[color:var(--c-ink)] border-[color:var(--c-border)] hover:border-[color:var(--c-ink)]"}`}
              >
                <Move size={11} /> {t("hover_zoom")} {hoverZoom ? t("on") : t("off")}
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="pv-in" style={{ animationDelay: "120ms" }}>
            <div className="flex flex-wrap gap-2 mb-4">
              {ad.era && <Badge className="rounded-none bg-[color:var(--c-border)] text-[color:var(--c-ink)] text-[0.6rem] tracking-widest uppercase">{ad.era}</Badge>}
              {ad.category && <Badge className="rounded-none bg-[color:var(--c-border)] text-[color:var(--c-ink)] text-[0.6rem] tracking-widest uppercase">{ad.category}</Badge>}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-[1.02] tracking-tight" data-testid="detail-title">{ad.title}</h1>
            <p className="font-serif text-4xl mt-5 text-[color:var(--c-accent)]" data-testid="detail-price">{formatPrice(ad.price_usd)}</p>

            <p className="mt-6 text-[color:var(--c-ink2)] leading-relaxed text-base" data-testid="detail-description">{ad.description}</p>

            <dl className="mt-8 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-[color:var(--c-border)] pt-8">
              {facts.map(([label, value]) => (
                <div key={label}>
                  <dt className="pv-label">{label}</dt>
                  <dd className="mt-1 text-[color:var(--c-ink)]">{value}</dd>
                </div>
              ))}
            </dl>

            <button
              data-testid="inquire-detail-button"
              onClick={() => setDialogOpen(true)}
              className="mt-10 w-full bg-[color:var(--c-dark)] text-[color:var(--c-on-dark)] hover:bg-[color:var(--c-accent)] transition-colors py-4 text-sm tracking-[0.2em] uppercase font-semibold"
            >
              {t("inquire")}
            </button>
          </div>
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="detail-lightbox"
          onClick={() => setLightbox(false)}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 pv-in"
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-[color:var(--c-accent)] text-white transition-colors"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <button
            data-testid="lightbox-zoom-toggle"
            onClick={(e) => { e.stopPropagation(); setLbHovering(false); setLbHoverEnabled((v) => !v); }}
            className={`absolute top-4 left-4 sm:top-6 sm:left-6 z-10 inline-flex items-center gap-2 px-4 py-2.5 text-[0.65rem] tracking-widest uppercase font-semibold transition-colors ${lbHoverEnabled ? "bg-[color:var(--c-accent)] text-white" : "bg-white/10 text-white hover:bg-white/20"}`}
            aria-label="Toggle hover zoom"
          >
            <ZoomIn size={14} /> {t("hover_zoom")}: {lbHoverEnabled ? t("on") : t("off")}
          </button>
          <div
            data-testid="lightbox-image-wrap"
            onClick={(e) => e.stopPropagation()}
            onMouseMove={lbMove}
            onMouseEnter={() => setLbHovering(true)}
            onMouseLeave={() => setLbHovering(false)}
            className={`relative overflow-hidden shadow-2xl ${lbHoverEnabled ? "cursor-zoom-in" : ""}`}
            style={{ maxHeight: "90vh", maxWidth: "92vw" }}
          >
            <img
              src={mediaUrl(ad.image)}
              alt={ad.title}
              draggable={false}
              data-testid="lightbox-image"
              style={{
                transformOrigin: `${lbPos.x}% ${lbPos.y}%`,
                transform: (lbHoverEnabled && lbHovering) ? "scale(2.4)" : "scale(1)",
                transition: "transform 160ms ease-out",
              }}
              className="block max-h-[90vh] max-w-[92vw] object-contain select-none"
            />
          </div>
        </div>
      )}

      <InquiryDialog ad={ad} open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
